import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/** How many years back the picker offers. */
const YEARS_OFFERED = 3;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `YYYY-MM` for a month index (0-based), e.g. (2026, 5) → "2026-06". */
export function periodKey(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

/** The month we default to: the one that just closed. */
export function currentPeriod(now: Date = new Date()): string {
  return periodKey(now.getFullYear(), now.getMonth());
}

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * "2026-06" → "June 2026", for header prose. The picker itself uses the
 * abbreviated {@link formatPeriod} so the trigger stays a fixed width.
 */
export function formatPeriodLong(period: string): string {
  const [year, month] = period.split('-');
  const idx = Number(month) - 1;
  return MONTHS_LONG[idx] ? `${MONTHS_LONG[idx]} ${year}` : period;
}

/** "2026-06" → "Jun 2026". Falls back to the raw value if it isn't a period. */
export function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  const idx = Number(month) - 1;
  return MONTHS[idx] ? `${MONTHS[idx]} ${year}` : period;
}

/**
 * Reporting-month selector: a popover with one compact 4×3 grid of months per
 * year, most recent year first.
 *
 * A grid rather than a `Select`: 3 years is 36 options, and as a vertical list
 * that's a long scroll where picking "March" means reading down a column of
 * near-identical rows. Laid out as a calendar-style grid, a month is a spatial
 * target — the same shape people already know from date pickers.
 *
 * Months after the current one are omitted: there is no data for a month that
 * hasn't happened, and offering them only invites empty dashboards.
 *
 * The selected period is owned by the route's search params, so the view is
 * bookmarkable and survives a reload.
 */
export function PeriodPicker({
  value,
  onChange,
  now = new Date(),
}: {
  value: string;
  onChange: (period: string) => void;
  /** Injectable for tests; defaults to today. */
  now?: Date;
}) {
  const [open, setOpen] = useState(false);
  const thisYear = now.getFullYear();
  const years = Array.from({ length: YEARS_OFFERED }, (_, i) => thisYear - i);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-[150px] justify-between font-normal"
          aria-label="Reporting month"
        >
          {formatPeriod(value)}
          <ChevronDown className="opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[248px] p-2">
        {years.map((year) => {
          const lastMonth = year < thisYear ? MONTHS.length - 1 : now.getMonth();
          return (
            <div key={year} className="mb-1 last:mb-0">
              <p className="px-1 py-1 font-medium text-muted-foreground text-xs">{year}</p>
              <div className="grid grid-cols-4 gap-1">
                {MONTHS.map((label, idx) => {
                  const key = periodKey(year, idx);
                  const selected = key === value;
                  const future = idx > lastMonth;
                  return (
                    <button
                      key={label}
                      type="button"
                      disabled={future}
                      aria-pressed={selected}
                      onClick={() => {
                        onChange(key);
                        setOpen(false);
                      }}
                      className={cn(
                        'rounded-md px-2 py-1.5 text-xs transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        future && 'cursor-not-allowed text-muted-foreground/40',
                        !future && !selected && 'hover:bg-accent hover:text-accent-foreground',
                        selected && 'bg-primary font-medium text-primary-foreground',
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
